import { Box, Button, Typography, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Header from "../../components/Header";
import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useResultService from "../../api/services/ResultsService";
import LoadingSpinner from "../../components/LoadingSpinner";
import ExportPDFButton from "../../components/ExportPDFButton";
import { VisibilityOutlined } from "@mui/icons-material";


const CORE_SUBJECTS = [
  "PHYSICS",
  "CHEMISTRY",
  "BIOLOGY",
  "ADVANCED MATHEMATICS",
  "ECONOMICS",
  "HISTORY",
  "GEOGRAPHY",
  "ENGLISH LANGUAGE",
];

// ---------- HELPERS ----------
const normalizeStatus = (v) => String(v ?? "").trim().toUpperCase();

const convert40To100 = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  return parseFloat(((num / 40) * 100).toFixed(1));
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (isNaN(num)) return "";
  return Number.isInteger(num) ? num.toString() : num.toFixed(1);
};

/** null / undefined / "" = no mark in DB (show nothing). 0 is a real stored mark. */
const parseBackendMark = (value) => {
  if (value === null || value === undefined) return null;
  if (value === "") return null;
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  return num;
};

const pickDisplayValue = (...values) =>
  values.find((value) => {
    const normalized = String(value ?? "").trim();
    return normalized && normalizeStatus(normalized) !== "N/A";
  }) || "";

// ---------- GRADES BASED ON CONVERTED /100 ----------
const getGrade = (marks100) => {
  if (marks100 >= 80) return "A";
  if (marks100 >= 70) return "B";
  if (marks100 >= 60) return "C";
  if (marks100 >= 50) return "D";
  if (marks100 >= 40) return "E";
  if (marks100 >= 35) return "S";
  return "F";
};

// ---------- DIVISION ----------
const getGradePoint = (grade) =>
  ({ A: 1, B: 2, C: 3, D: 4, E: 5, S: 6, F: 7 }[grade] || 0);

const determineDivisionPoints = (subjectsObj) => {
  const points = Object.entries(subjectsObj)
    .filter(
      ([subject, subjectData]) =>
        CORE_SUBJECTS.includes(subject.toUpperCase()) &&
        subjectData?.final?.convertedValue !== null &&
        subjectData?.final?.convertedValue !== undefined
    )
    .map(([_, subjectData]) => getGradePoint(subjectData.final.grade || "F"));

  if (points.length === 0) return 0;

  const usePoints =
    points.length >= 3 ? [...points].sort((a, b) => a - b).slice(0, 3) : points;

  return usePoints.reduce((sum, point) => sum + point, 0);
};

const getDivisionLabel = (points) => {
  const p = parseFloat(points).toFixed(1).replace(/\.0$/, "");
  if (points >= 1 && points <= 9) return `Division 1 .${p}`;
  if (points >= 10 && points <= 12) return `Division 2 .${p}`;
  if (points >= 13 && points <= 17) return `Division 3 .${p}`;
  if (points >= 18 && points <= 21) return `Division 4 .${p}`;
  return `Division 0 .${p}`;
};

const AlevelClassResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const classId = location.state?.classId || location.state?.id;
  const examinationTypeId = location.state?.examId;

  const { fetchClassResults, sendSmsToParents } = useResultService();

  const [results, setResults] = useState(null);
  const [groupedResults, setGroupedResults] = useState([]);
  const [sendingStatus, setSendingStatus] = useState({});
  const [sentStatus, setSentStatus] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // ACTIVE / ALL
  const [statusFilter, setStatusFilter] = useState("ACTIVE");

  // marks view: default is 40
  const [marksView, setMarksView] = useState("40");

  // keep latest service refs
  const fetchClassResultsRef = useRef(fetchClassResults);
  const sendSmsToParentsRef = useRef(sendSmsToParents);

  useEffect(() => {
    fetchClassResultsRef.current = fetchClassResults;
  }, [fetchClassResults]);

  useEffect(() => {
    sendSmsToParentsRef.current = sendSmsToParents;
  }, [sendSmsToParents]);

  useEffect(() => {
    const savedSentStatus = localStorage.getItem(
      `sentStatus_${classId}_${examinationTypeId}`
    );
    if (savedSentStatus) {
      setSentStatus(JSON.parse(savedSentStatus));
    }
  }, [classId, examinationTypeId]);

  const handleViewStudent = (row) => {
    navigate(`/alevel_student_results/${row.id}/${classId}/${examinationTypeId}`);
  };

  useEffect(() => {
    let cancelled = false;

    const loadResults = async () => {
      if (!classId || !examinationTypeId) return;

      setIsLoading(true);
      try {
        const data = await fetchClassResultsRef.current(classId, examinationTypeId);
        if (!cancelled) setResults(data);
      } catch (e) {
        if (!cancelled) setResults(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadResults();

    return () => {
      cancelled = true;
    };
  }, [classId, examinationTypeId]);

  const calculateGeneralValue = (subject, parts) => {
    if (!parts?.length) return null;

    const partValues = parts.map((part) => parseBackendMark(part.marks));
    const numericOnly = partValues.filter((m) => m !== null);

    if (numericOnly.length === 0) {
      return {
        value: null,
        rawValue: null,
        convertedValue: null,
        grade: "",
      };
    }

    const total = numericOnly.reduce((sum, m) => sum + m, 0);
    const rawValue = parseFloat(total.toFixed(1));
    const convertedValue = convert40To100(rawValue);
    const grade = convertedValue !== null ? getGrade(convertedValue) : "";

    return {
      value: rawValue,
      rawValue,
      convertedValue,
      grade,
    };
  };

  const groupSubjects = (subjects) => {
    const grouped = {};
    Object.entries(subjects).forEach(([subject, data]) => {
      const base = CORE_SUBJECTS.find((s) => subject.startsWith(s));
      if (base) {
        if (!grouped[base]) grouped[base] = [];
        grouped[base].push({ name: subject, ...data });
      } else {
        grouped[subject] = data;
      }
    });
    return grouped;
  };

  const processStudent = (studentData) => {
    const groupedSubjects = groupSubjects(studentData.subjects);
    const finalSubjects = {};

    Object.entries(groupedSubjects).forEach(([subject, data]) => {
      if (Array.isArray(data)) {
        const parts = data.map((part) => {
          const rawMarks = parseBackendMark(part.marks);

          return {
            ...part,
            rawMarks,
            convertedMarks:
              rawMarks !== null && !isNaN(rawMarks)
                ? convert40To100(rawMarks)
                : null,
          };
        });

        const result = calculateGeneralValue(subject, parts);

        finalSubjects[subject] = {
          parts,
          final: result,
        };
      } else {
        const rawMarks = parseBackendMark(data.marks);

        const convertedMarks =
          rawMarks !== null && !isNaN(rawMarks) ? convert40To100(rawMarks) : null;

        const grade = convertedMarks !== null ? getGrade(convertedMarks) : "";

        finalSubjects[subject] = {
          parts: [
            {
              name: subject,
              ...data,
              rawMarks,
              convertedMarks,
            },
          ],
          final: {
            value: rawMarks,
            rawValue: rawMarks,
            convertedValue: convertedMarks,
            grade,
          },
        };
      }
    });

    const finalValues = Object.values(finalSubjects);

    const rawSubjectValues = finalValues
      .map((sub) => sub?.final?.rawValue)
      .filter((v) => v !== undefined && v !== null && !isNaN(v)); // includes 0

    const convertedSubjectValues = finalValues
      .map((sub) => sub?.final?.convertedValue)
      .filter((v) => v !== undefined && v !== null && !isNaN(v));

    const totalMarks40 = parseFloat(
      rawSubjectValues.reduce((sum, v) => sum + v, 0).toFixed(1)
    );

    const totalMarks100 = parseFloat(
      convertedSubjectValues.reduce((sum, v) => sum + v, 0).toFixed(1)
    );

    const subjectCount = rawSubjectValues.length;

    const average40 =
      subjectCount > 0 ? parseFloat((totalMarks40 / subjectCount).toFixed(1)) : 0;

    const average100 =
      subjectCount > 0 ? parseFloat((totalMarks100 / subjectCount).toFixed(1)) : 0;

    const divisionPoints = determineDivisionPoints(finalSubjects);
    const division =
      subjectCount < 4 ? "Incomplete" : getDivisionLabel(divisionPoints);

    return {
      id: studentData.id,
      studentName: studentData.studentName,
      studentNumber: studentData.studentNumber,
      combination: studentData.combination,
      position: 0,
      totalMarks40,
      totalMarks100,
      average40,
      average100,
      subjects: finalSubjects,
      division,
      status: studentData.status,
    };
  };

  const groupResultsByStudent = (data) => {
    const studentMap = {};

    data.forEach((item) => {
      if (!studentMap[item.studentId]) {
        studentMap[item.studentId] = {
          id: item.studentId,
          studentName: item.studentName,
          studentNumber: item.studentNumber,
          combination: item.combination,
          position: item.position,
          subjects: {},
          status: item.studentStatus || item.status,
        };
      }

      studentMap[item.studentId].subjects[item.subjectName] = {
        marks: item.marks,
      };
    });

    return Object.values(studentMap);
  };

  useEffect(() => {
    if (results && results.data && Array.isArray(results.data) && results.data.length > 0) {
      const grouped = groupResultsByStudent(results.data);

      const processed = grouped
        .map((student) => processStudent(student))
        .filter((student) =>
          Object.values(student.subjects || {}).some(
            (sub) =>
              (sub?.final?.rawValue !== undefined && sub?.final?.rawValue !== null) ||
              (sub?.parts && sub.parts.length > 0)
          )
        )
        .sort((a, b) => {
          if ((b.average100 || 0) === (a.average100 || 0)) {
            return (b.totalMarks100 || 0) - (a.totalMarks100 || 0);
          }
          return (b.average100 || 0) - (a.average100 || 0);
        })
        .map((student, index) => ({
          ...student,
          position: index + 1,
        }));

      const firstResult = results.data?.[0];
      const className = pickDisplayValue(
        results?.className,
        firstResult?.className,
        location.state?.className
      );
      const classTitle = pickDisplayValue(
        results?.classTitle,
        firstResult?.classTitle,
        location.state?.classTitle,
        className
      );
      const examinationType = pickDisplayValue(
        results?.examinationType,
        firstResult?.examinationType,
        location.state?.examinationType
      );

      const processedWithDetails = processed.map((student) => ({
        ...student,
        className,
        classTitle,
        examinationType,
      }));

      setGroupedResults(processedWithDetails);
    } else {
      setGroupedResults([]);
    }
  }, [results]);

  const filteredResults = useMemo(() => {
    if (!groupedResults?.length) return [];

    if (statusFilter === "ACTIVE") {
      return groupedResults.filter((row) => normalizeStatus(row.status) === "ACTIVE");
    }

    return groupedResults;
  }, [groupedResults, statusFilter]);

  const displayResults = useMemo(() => {
    return filteredResults.map((student, index) => ({
      ...student,
      position: index + 1,
      totalMarks: marksView === "40" ? student.totalMarks40 : student.totalMarks100,
      average: marksView === "40" ? student.average40 : student.average100,
    }));
  }, [filteredResults, marksView]);

  const handleSendSMS = async (row) => {
    const studentId = row.id;
    setSendingStatus((prev) => ({ ...prev, [studentId]: true }));

    try {
      const subjectsSummary = Object.entries(row.subjects).reduce(
        (acc, [subject, details]) => {
          const marks =
            marksView === "40"
              ? details?.final?.rawValue
              : details?.final?.convertedValue;
          if (marks === undefined || marks === null) return acc;
          acc[subject] = {
            marks: parseFloat(marks) || 0,
            grade: details?.final?.grade || "",
          };
          return acc;
        },
        {}
      );

      const payload = {
        studentName: row.studentName,
        studentNumber: row.studentNumber,
        subjects: subjectsSummary,
        totalMarks: parseFloat(row.totalMarks) || 0,
        average: parseFloat(row.average?.toFixed?.(1)) || 0,
        position: row.position ? parseInt(row.position) : 0,
        division: row.division || "N/A",
      };

      await sendSmsToParentsRef.current(studentId, payload);

      setSentStatus((prev) => {
        const newStatus = { ...prev, [studentId]: true };
        localStorage.setItem(
          `sentStatus_${classId}_${examinationTypeId}`,
          JSON.stringify(newStatus)
        );
        return newStatus;
      });
    } catch (error) {
      console.error("Error sending SMS", error);
    } finally {
      setSendingStatus((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const activeCount = useMemo(() => {
    return groupedResults.filter((row) => normalizeStatus(row.status) === "ACTIVE").length;
  }, [groupedResults]);

  const totalCount = groupedResults?.length ?? 0;

  const createColumns = (rows) => {
    if (!rows.length) return [];

    const subjectSet = new Set();
    rows.forEach((student) =>
      Object.keys(student.subjects || {}).forEach((subj) => subjectSet.add(subj))
    );
    const subjectNames = Array.from(subjectSet);

    const columns = [
      {
        field: "studentName",
        headerName: "Name",
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.studentName}</Typography>
        ),
      },
      {
        field: "studentNumber",
        headerName: "Index Number",
        flex: 1,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.studentNumber}</Typography>
        ),
      },
      {
        field: "combination",
        headerName: "Combination",
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => (
          <Typography>{row.combination ?? ""}</Typography>
        ),
      },
    ];

    subjectNames.forEach((subject) => {
      columns.push({
        field: `subject_${subject}`,
        headerName: subject,
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }) => {
          const subj = row.subjects?.[subject];
          if (!subj) return "";

          const finalMarks =
            marksView === "40" ? subj?.final?.rawValue : subj?.final?.convertedValue;

          const grade = subj?.final?.grade || "";

          if (finalMarks === undefined || finalMarks === null || finalMarks === "") {
            return "";
          }

          return (
            <Box>
              {marksView === "40" &&
                (subj.parts || []).length > 1 &&
                subj.parts.map((part, idx) => {
                  const partMarks = part?.rawMarks;
                  if (
                    partMarks === undefined ||
                    partMarks === null ||
                    partMarks === ""
                  ) {
                    return null;
                  }

                  return (
                    <Typography key={idx}>
                      {part.name}: {formatNumber(partMarks)}
                    </Typography>
                  );
                })}

              <Box mt="5px" borderTop="1px solid #ccc" pt="5px">
                <Typography fontWeight="bold">
                  Marks: {formatNumber(finalMarks)}
                  {marksView === "100" && grade ? ` (${grade})` : ""}
                </Typography>
              </Box>
            </Box>
          );
        },
      });
    });

    columns.push(
      {
        field: "totalMarks",
        headerName: marksView === "40" ? "Total Marks /40" : "Total Marks /100",
        flex: 0.8,
        minWidth: 130,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{formatNumber(row.totalMarks)}</Typography>
        ),
      },
      {
        field: "average",
        headerName: marksView === "40" ? "Average /40" : "Average /100",
        flex: 0.7,
        minWidth: 110,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{formatNumber(row.average)}</Typography>
        ),
      },
      {
        field: "division",
        headerName: "Division",
        flex: 0.8,
        minWidth: 130,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">{row.division || ""}</Typography>
        ),
      },
      {
        field: "position",
        headerName: "Position",
        flex: 0.7,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Typography fontWeight="bold">
            {row.position} out of {rows.length}
          </Typography>
        ),
      },
      {
        field: "action",
        headerName: "Action",
        flex: 0.8,
        minWidth: 150,
        renderCell: ({ row }) => (
          <Box display="flex" gap={1} alignItems="center">
            <IconButton color="secondary" onClick={() => handleViewStudent(row)}>
              <VisibilityOutlined />
            </IconButton>

            <Button
              variant="contained"
              size="small"
              disabled={sendingStatus[row.id]}
              onClick={() => handleSendSMS(row)}
              sx={{
                backgroundColor: sentStatus[row.id] ? "#0A6927" : undefined,
                color: sentStatus[row.id] ? "white" : undefined,
                "&:hover": {
                  backgroundColor: sentStatus[row.id] ? "#085A1F" : undefined,
                },
                "&:disabled": {
                  backgroundColor: sendingStatus[row.id] ? "#ccc" : undefined,
                },
              }}
            >
              {sendingStatus[row.id]
                ? "Sending..."
                : sentStatus[row.id]
                ? "Sent"
                : "Send SMS"}
            </Button>
          </Box>
        ),
      }
    );

    return columns;
  };

  const gridColumns = useMemo(
    () => createColumns(displayResults),
    [displayResults, marksView, sendingStatus, sentStatus]
  );

  const classNameForHeader = pickDisplayValue(
    groupedResults[0]?.className,
    location.state?.className
  );
  const classTitleForHeader = pickDisplayValue(
    groupedResults[0]?.classTitle,
    location.state?.classTitle,
    classNameForHeader
  );
  const examNameForHeader = pickDisplayValue(
    groupedResults[0]?.examinationType,
    location.state?.examinationType
  );

  return (
    <Box m="20px">
      <Box p={2} ml={2} mr={2}>
        <Header
          title={`CLASS RESULTS FOR CLASS NAME: ${classNameForHeader} | CLASS TITLE: ${classTitleForHeader} | EXAM-NAME: ${examNameForHeader}`}
        />

        <Box
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              color="secondary"
              onClick={() => setStatusFilter("ACTIVE")}
              variant={statusFilter === "ACTIVE" ? "contained" : "outlined"}
              size="small"
            >
              ACTIVE ({activeCount})
            </Button>

            <Button
              color="secondary"
              onClick={() => setStatusFilter("ALL")}
              variant={statusFilter === "ALL" ? "contained" : "outlined"}
              size="small"
            >
              ALL ({totalCount})
            </Button>

            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={() =>
                setMarksView((prev) => (prev === "100" ? "40" : "100"))
              }
            >
              {marksView}
            </Button>
          </Box>

          <ExportPDFButton
            groupedResults={displayResults}
            isClassResults={true}
            className={classNameForHeader}
            classTitle={classTitleForHeader}
            examinationType={examNameForHeader}
            marksView={marksView}
          />
        </Box>

        {isLoading ? (
          <LoadingSpinner />
        ) : displayResults.length > 0 ? (
          <Box
            sx={{
              height: "75vh",
              "& .MuiDataGrid-root": { border: "none" },
              "& .MuiDataGrid-cell": {
                whiteSpace: "normal",
                wordWrap: "break-word",
                lineHeight: "1.4rem",
              },
            }}
          >
            <DataGrid
              rows={displayResults}
              columns={gridColumns}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              getRowHeight={() => 120}
            />
          </Box>
        ) : (
          <Typography variant="h6" color="textSecondary" textAlign="center" mt={4}>
            No results found
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AlevelClassResults;